import Service, { type Registry as Services, service } from '@ember/service';
import {
  AnalyticsBrowser,
  type EventProperties as SegmentEventProperties,
  type MiddlewareFunction,
} from '@segment/analytics-next';
import ENV from 'frontend-payments/config/environment';
import mixpanel from 'mixpanel-browser';

export interface EventProperties extends SegmentEventProperties {
  eventTrigger: string;
}

// Session replay recording percentage for frontend-payments
const MIXPANEL_SESSION_REPLAY_CONFIG = {
  record_sessions_percent: 100, // Record 100% of sessions
};

const mixpanelSessionReplayMiddleware: MiddlewareFunction = ({
  payload,
  next,
}): void => {
  const obj = payload.obj;
  if (obj.type === 'track' || obj.type === 'page') {
    if (mixpanel) {
      const segmentDeviceId = obj.anonymousId;
      // Original ID Merge
      mixpanel.register({
        $device_id: segmentDeviceId,
        distinct_id: segmentDeviceId,
      });
      const sessionReplayProperties =
        mixpanel.get_session_recording_properties();
      obj.properties = {
        ...obj.properties,
        ...sessionReplayProperties,
      };
    }
  }

  if (obj.type === 'identify') {
    if (mixpanel) {
      const { userId } = obj;
      if (userId !== null) {
        mixpanel.identify(userId);
      }
    }
  }

  next(payload);
};

export default class UsageAnalyticsService extends Service {
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];
  @service declare session: Services['session'];

  analytics: AnalyticsBrowser | null = null;
  isEnabledInEnvironment: boolean = ENV.analytics.enabled;
  isLoggingEnabled: boolean = ENV.analytics.enableLogging;
  isSessionReplayInitialised = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get traitsGlobal() {
    const { userAgent } = navigator;
    // Pass all mobile devices
    // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
    const isMobileBrowser =
      userAgent.includes('Mobi') ||
      userAgent.includes('Android') ||
      userAgent.includes('iPhone') ||
      userAgent.includes('iPad');

    return {
      isMobileBrowser,
      userId: this.session.user?.email,
      isStaffUser: this.session.isStaffUser,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get traitsShared() {
    return {
      // Mixpanel requires that we send some traits with every `track` & `page` event,
      // in order to correctly group and filter events.
      product: 'payments',
      subscriptionId: null,
      marketId: null,
      hotelinfoId: null,
    };
  }

  identify(email: string, checkForStaffUser = false): void {
    if (!this.isEnabledInEnvironment) {
      return;
    }

    try {
      if (!this.analytics) {
        throw new Error(
          'usage-analytics is not initialised while calling identify',
        );
      }

      // Determine staff user status: check email if requested, otherwise use session value
      const staffUserValue = checkForStaffUser
        ? email.endsWith('@mylighthouse.com')
        : this.session.isStaffUser;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.analytics.identify(email, {
        email,
        ...this.traitsShared,
        ...this.traitsGlobal,
        isStaffUser: staffUserValue,
      });

      // Set Mixpanel people properties for filtering session replays
      if (mixpanel && mixpanel.people) {
        mixpanel.people.set({
          $email: email,
          isStaffUser: staffUserValue,
          product: 'payments',
        });
      }
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  initialise(): void {
    if (!this.isEnabledInEnvironment) {
      return;
    }

    try {
      this.analytics = AnalyticsBrowser.load(
        {
          writeKey: ENV.analytics.writeKey,
          cdnURL: ENV.analytics.cdnURL,
        },
        {
          integrations: {
            'Segment.io': {
              apiHost: ENV.analytics.apiHost,
              protocol: 'https',
            },
          },
          useQueryString: false,
        },
      );
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  /*
    Initialize Mixpanel session replay for the frontend-payments app.
    `isManualTrigger` can be used to force recording from e.g. a button click.
    When true, we pass 100% as session recording percentage.
  */
  initialiseSessionReplay(isManualTrigger?: boolean): void {
    if (!this.isEnabledInEnvironment) {
      return;
    }

    try {
      if (!this.analytics) {
        this.errorTracking.addBreadcrumb({
          message:
            'Tried to initialize Mixpanel without Segment being initialized.',
        });
        return;
      }

      if (!this.isSessionReplayInitialised || isManualTrigger) {
        // Consider this done even if it fails, so we don't try to initialize it again.
        this.isSessionReplayInitialised = true;

        // https://docs.mixpanel.com/docs/session-replay/implement-session-replay/session-replay-web#init-options
        mixpanel.init(ENV['mixpanel-browser'].token, {
          api_host: ENV['mixpanel-browser'].apiHost,
          record_sessions_percent: isManualTrigger
            ? 100
            : MIXPANEL_SESSION_REPLAY_CONFIG.record_sessions_percent,
          record_heatmap_data: true,
          record_mask_text_selector: '', // https://github.com/mixpanel/docs/blob/main/pages/docs/session-replay/session-replay-privacy-controls.mdx#configuring-privacy-controls
        });

        // Middleware to add Mixpanel's session recording properties to Segment events
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.analytics.addSourceMiddleware(mixpanelSessionReplayMiddleware);
      }
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  page(): void {
    if (!this.isEnabledInEnvironment) {
      return;
    }

    try {
      if (!this.analytics) {
        throw new Error(
          'usage-analytics is not initialised while calling page',
        );
      }

      const { currentRouteName } = this.router;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.analytics.page(
        { ...this.traitsShared, ...this.traitsGlobal, route: currentRouteName },
        {
          context: {
            traits: {
              ...this.traitsShared,
              route: currentRouteName,
            },
          },
        },
      );
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  track(event: string, properties: EventProperties): void {
    if (!this.isLoggingEnabled && !this.isEnabledInEnvironment) {
      return;
    }

    const allProperties = {
      ...this.traitsShared,
      ...this.traitsGlobal,
      ...properties,
    };

    if (this.isLoggingEnabled) {
      // Log to the console if logging is enabled, so we can test the events in the browser during development.
      console.info('Track', event, allProperties);
    }

    if (!this.isEnabledInEnvironment) {
      return;
    }

    try {
      if (!this.analytics) {
        throw new Error(
          'usage-analytics is not initialised while calling track',
        );
      }

      void this.analytics.track(event, allProperties);
    } catch (error) {
      this.errorTracking.captureException(error, { event, properties });
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'usage-analytics': UsageAnalyticsService;
  }
}
